import { useEffect, useState } from "react"
import { InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import { confirm } from "lib/@dialogs/confirm"

export function TimeModifierInput({ timeKind, timeValue, onChange }) {
    const kind = timeKind === "percentage" ? "percentage" : "minutes"
    const suffix = getSuffix(kind)
    const displayValue = getDisplayValue(kind, timeValue)
    const [localValue, setLocalValue] = useState(String(displayValue))

    useEffect(syncFromProps, [kind, timeValue])

    return (
        <Stack direction="row" alignItems="center" spacing={1} data-cy="time-modifier-input">
            <ToggleButtonGroup exclusive size="small" value={kind} onChange={handleKindChange}>
                <ToggleButton value="minutes">min</ToggleButton>
                <ToggleButton value="percentage">%</ToggleButton>
            </ToggleButtonGroup>
            <TextField
                size="small"
                type="number"
                value={localValue}
                onChange={handleValueChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onWheel={handleWheel}
                inputProps={{ step: 1 }}
                sx={{ width: 110 }}
                InputProps={{
                    endAdornment: <InputAdornment position="end">{suffix}</InputAdornment>,
                }}
            />
            <Typography variant="caption" color="text.secondary">
                {kindCaption(kind, localValue)}
            </Typography>
        </Stack>
    )

    function syncFromProps() {
        setLocalValue(String(getDisplayValue(kind, timeValue)))
    }

    function handleKindChange(_, newKind) {
        if (!newKind || newKind === kind) return
        const numeric = parseFloat(localValue)
        const effective = Number.isFinite(numeric) ? numeric : 0
        const storedValue = newKind === "percentage" ? effective / 100 : effective
        applyChange(newKind, storedValue)
    }

    function handleFocus(e) {
        e.target.select()
    }

    function handleWheel(e) {
        e.target.blur()
    }

    function handleValueChange(e) {
        setLocalValue(e.target.value)
    }

    async function handleBlur() {
        const numeric = parseFloat(localValue)
        const effective = Number.isFinite(numeric) ? numeric : 0
        const storedValue = kind === "percentage" ? effective / 100 : effective
        await applyChange(kind, storedValue)
    }

    async function applyChange(nextKind, nextValue) {
        if (nextKind === "minutes" && nextValue < 0) {
            const ok = await confirm(
                "A negative minute value will shorten task duration. Are you sure?",
                "Negative Time Modifier"
            )
            if (!ok) {
                syncFromProps()
                return
            }
        }
        if (nextKind === "percentage" && nextValue > 5) {
            const ok = await confirm(
                `A +${Math.round(nextValue * 100)}% time uplift is very high. Are you sure?`,
                "High Time Modifier"
            )
            if (!ok) {
                syncFromProps()
                return
            }
        }
        onChange({ timeKind: nextKind, timeValue: nextValue })
    }
}

function getDisplayValue(kind, timeValue) {
    if (kind === "percentage") return Math.round((timeValue ?? 0) * 100)
    return timeValue ?? 0
}

function getSuffix(kind) {
    if (kind === "percentage") return "%"
    return "min"
}

function kindCaption(kind, display) {
    if (kind === "percentage") {
        const pct = Number(display) || 0
        const sign = pct >= 0 ? "+" : ""
        return `= ${sign}${pct}% duration`
    }
    const mins = Number(display) || 0
    const sign = mins >= 0 ? "+" : ""
    return `= ${sign}${mins} min/task`
}
